import fetchAPI from '_lib/api/fetchWrapper';
import apiRoute from '_lib/api/paths';
import { PayloadAction } from '@reduxjs/toolkit';
import { createSliceSaga, SagaType } from 'redux-toolkit-saga';
import { put } from 'typed-redux-saga';

import heatmapReducer, { HeatmapRequest, VehicleCountsRequest } from './heatmapReducers';
import {
  findCovering,
  getDataset,
  getVehicleCounts,
  GridApiResponse,
  heatmapKey,
  parseGridResponse,
  parseVehicleCounts,
  setDataset,
  setVehicleCounts,
  VehicleCountsResponse,
  vehicleKey,
} from './utils';

export function* getHeatmap(action: PayloadAction<HeatmapRequest>) {
  const { metric, start, end } = action.payload;
  const key = heatmapKey(metric, start, end);
  try {
    const wider = getDataset(key) ? null : findCovering(metric, start, end);
    if (wider) {
      setDataset(wider.ds, start, end, wider.fetchedAt); // sub-range of a cached interval: no request
    } else if (!getDataset(key)) {
      const startIso = new Date(start).toISOString();
      const endIso = new Date(end).toISOString();
      const url =
        metric === 'counter'
          ? apiRoute.camsGridVehicleCounts(startIso, endIso)
          : apiRoute.camsGridSpeedValues(startIso, endIso);
      const res: GridApiResponse = yield fetchAPI(url);
      setDataset(parseGridResponse(res, metric), start, end);
    }
    yield* put(heatmapReducer.actions.heatmapLoaded(key));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('ERROR getHeatmap >>>>>>> ', e);
    const message = e?.packet?.message ?? e?.response?.statusText ?? e?.message ?? 'Unknown error';
    yield* put(heatmapReducer.actions.heatmapFailed(String(message)));
  }
}

// Own saga: COUNT(DISTINCT) is slow on big ranges and must not hold up the map
export function* getVehicleCountsSaga(action: PayloadAction<VehicleCountsRequest>) {
  const { start, end } = action.payload;
  try {
    let key = vehicleKey(start, end);
    if (!getVehicleCounts(key)) {
      const res: VehicleCountsResponse = yield fetchAPI(
        apiRoute.camsVehicleCountsOverTime(new Date(start).toISOString(), new Date(end).toISOString()),
      );
      key = setVehicleCounts(start, end, parseVehicleCounts(res));
    }
    yield* put(heatmapReducer.actions.vehicleCountsLoaded(key));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('ERROR getVehicleCounts >>>>>>> ', e);
    yield* put(heatmapReducer.actions.vehicleCountsFailed());
  }
}

// TakeLatest: changing the range/metric quickly drops the stale response instead of racing it
export const heatmapSagas = createSliceSaga({
  name: 'heatmap',
  caseSagas: {
    heatmapRequest: getHeatmap,
    vehicleCountsRequest: getVehicleCountsSaga,
  },
  sagaType: SagaType.TakeLatest,
});

export default heatmapSagas;
